import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Calculator, Aperture, Focus, Clock, Star, Ruler
} from 'lucide-react';
import { useI18n } from '../context/I18nContext';

// Exposure Calculator
const ExposureCalculator = () => {
  const { t } = useI18n();
  const [iso, setIso] = useState(100);
  const [aperture, setAperture] = useState(8);
  const [ev, setEv] = useState(15); // Sunny 16 = EV 15

  const evDescriptions = {
    16: t('calc.evBrightSunOnSnow'),
    15: t('calc.evSunny'),
    14: t('calc.evSlightOvercast'),
    13: t('calc.evOvercast'),
    12: t('calc.evHeavyOvercast'),
    11: t('calc.evOpenShade'),
    10: t('calc.evBrightIndoor'),
    8: t('calc.evIndoor'),
    6: t('calc.evDimIndoor'),
    4: t('calc.evCandlelight'),
    2: t('calc.evNightStreet'),
    0: t('calc.evFullMoon'),
    '-2': t('calc.evQuarterMoon'),
    '-4': t('calc.evStarlight'),
    '-6': t('calc.evMilkyWay')
  };

  const shutterSpeed = useMemo(() => {
    // EV = log2(N²/t) where N is f-number, t is time in seconds
    // At ISO 100: t = N² / 2^EV
    const isoFactor = 100 / iso;
    const time = (aperture * aperture) / Math.pow(2, ev) * isoFactor;

    if (time >= 60) return `${Math.round(time / 60)}min`;
    if (time >= 1) return `${time.toFixed(1)}s`;
    if (time >= 0.5) return `1/${Math.round(1/time)}`;
    if (time >= 0.1) return `1/${Math.round(1/time)}`;
    return `1/${Math.round(1/time)}`;
  }, [iso, aperture, ev]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-400 text-xs">{t('calc.iso')}</Label>
          <Input
            type="number"
            value={iso}
            onChange={(e) => setIso(Number(e.target.value))}
            className="bg-slate-800 border-white/10 font-mono"
            data-testid="exposure-iso"
          />
        </div>
        <div>
          <Label className="text-slate-400 text-xs">{t('calc.apertureF')}</Label>
          <Input
            type="number"
            step="0.1"
            value={aperture}
            onChange={(e) => setAperture(Number(e.target.value))}
            className="bg-slate-800 border-white/10 font-mono"
            data-testid="exposure-aperture"
          />
        </div>
      </div>

      <div>
        <Label className="text-slate-400 text-xs">{t('calc.lightCondition')}</Label>
        <Slider
          value={[ev]}
          onValueChange={([v]) => setEv(v)}
          min={-6}
          max={16}
          step={1}
          className="my-3"
        />
        <p className="text-sm text-slate-300">{evDescriptions[ev] || `${t('calc.ev')} ${ev}`}</p>
      </div>

      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
        <p className="text-xs text-slate-400 mb-1">{t('calc.shutterSpeed')}</p>
        <p className="font-mono text-3xl text-amber-500">{shutterSpeed}</p>
      </div>
    </div>
  );
};

// DOF Calculator
const DOFCalculator = () => {
  const { t } = useI18n();
  const [focalLength, setFocalLength] = useState(50);
  const [aperture, setAperture] = useState(2.8);
  const [distance, setDistance] = useState(3);
  const [sensorSize, setSensorSize] = useState(36); // Full frame

  const dofResults = useMemo(() => {
    // Circle of confusion (CoC) based on sensor size
    const coc = sensorSize / 1500; // Simplified calculation

    // Hyperfocal distance
    const hyperfocal = (focalLength * focalLength) / (aperture * coc) / 1000 + focalLength / 1000;

    // Near and far focus distances
    const near = (distance * (hyperfocal - focalLength / 1000)) /
                 (hyperfocal + distance - 2 * focalLength / 1000);
    const far = (distance * (hyperfocal - focalLength / 1000)) /
                (hyperfocal - distance);

    const dof = far > 0 ? far - near : Infinity;

    return {
      near: near > 0 ? near.toFixed(2) : '0',
      far: far > 0 && far < 10000 ? far.toFixed(2) : '∞',
      total: dof > 0 && dof < 10000 ? dof.toFixed(2) : '∞',
      hyperfocal: hyperfocal.toFixed(2)
    };
  }, [focalLength, aperture, distance, sensorSize]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-400 text-xs">{t('calc.focalLengthMm')}</Label>
          <Input
            type="number"
            value={focalLength}
            onChange={(e) => setFocalLength(Number(e.target.value))}
            className="bg-slate-800 border-white/10 font-mono"
            data-testid="dof-focal"
          />
        </div>
        <div>
          <Label className="text-slate-400 text-xs">{t('calc.apertureF')}</Label>
          <Input
            type="number"
            step="0.1"
            value={aperture}
            onChange={(e) => setAperture(Number(e.target.value))}
            className="bg-slate-800 border-white/10 font-mono"
            data-testid="dof-aperture"
          />
        </div>
        <div>
          <Label className="text-slate-400 text-xs">{t('calc.distanceM')}</Label>
          <Input
            type="number"
            step="0.1"
            value={distance}
            onChange={(e) => setDistance(Number(e.target.value))}
            className="bg-slate-800 border-white/10 font-mono"
            data-testid="dof-distance"
          />
        </div>
        <div>
          <Label className="text-slate-400 text-xs">{t('calc.sensorMm')}</Label>
          <Input
            type="number"
            value={sensorSize}
            onChange={(e) => setSensorSize(Number(e.target.value))}
            className="bg-slate-800 border-white/10 font-mono"
            data-testid="dof-sensor"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-lg bg-slate-800/50">
          <p className="text-xs text-slate-500">{t('calc.nearFocus')}</p>
          <p className="font-mono text-lg text-white">{dofResults.near}m</p>
        </div>
        <div className="p-3 rounded-lg bg-slate-800/50">
          <p className="text-xs text-slate-500">{t('calc.farFocus')}</p>
          <p className="font-mono text-lg text-white">{dofResults.far}m</p>
        </div>
      </div>

      <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
        <p className="text-xs text-slate-400 mb-1">{t('calc.totalDof')}</p>
        <p className="font-mono text-3xl text-emerald-500">{dofResults.total}m</p>
      </div>

      <div className="p-3 rounded-lg bg-slate-800/50">
        <p className="text-xs text-slate-500">{t('calc.hyperfocalDistance')}</p>
        <p className="font-mono text-lg text-white">{dofResults.hyperfocal}m</p>
        <p className="text-xs text-slate-600 mt-1">
          {t('calc.focusHereFrom')} {(dofResults.hyperfocal / 2).toFixed(1)}m {t('calc.toInfinity')}
        </p>
      </div>
    </div>
  );
};

// Star Trails Calculator
const StarTrailsCalculator = () => {
  const { t } = useI18n();
  const [focalLength, setFocalLength] = useState(24);
  const [cropFactor, setCropFactor] = useState(1);

  const results = useMemo(() => {
    const effectiveFocal = focalLength * cropFactor;

    // 500 Rule (conservative)
    const rule500 = 500 / effectiveFocal;

    // NPF Rule (more accurate)
    // t = (35 × aperture + 30 × pixel_pitch) ÷ focal_length
    // Simplified using average values
    const ruleNPF = (35 * 2.8 + 30 * 0.005) / effectiveFocal * 10;

    // Star trails need at least 20-30 minutes
    const minTrailTime = 20;
    const mediumTrailTime = 60;
    const longTrailTime = 120;

    return {
      noTrails: Math.round(rule500),
      npfRule: Math.round(ruleNPF),
      shortTrails: minTrailTime,
      mediumTrails: mediumTrailTime,
      longTrails: longTrailTime,
      shotsNeeded: (mediumTrailTime * 60) / rule500
    };
  }, [focalLength, cropFactor]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-400 text-xs">{t('calc.focalLengthMm')}</Label>
          <Input
            type="number"
            value={focalLength}
            onChange={(e) => setFocalLength(Number(e.target.value))}
            className="bg-slate-800 border-white/10 font-mono"
            data-testid="trails-focal"
          />
        </div>
        <div>
          <Label className="text-slate-400 text-xs">{t('calc.cropFactor')}</Label>
          <Input
            type="number"
            step="0.1"
            value={cropFactor}
            onChange={(e) => setCropFactor(Number(e.target.value))}
            className="bg-slate-800 border-white/10 font-mono"
            data-testid="trails-crop"
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <p className="text-xs text-slate-400">{t('calc.pointStars')}</p>
          <p className="font-mono text-2xl text-blue-400">{results.noTrails}s</p>
          <p className="text-xs text-slate-500">{t('calc.rule500')}</p>
        </div>

        <div className="p-3 rounded-lg bg-slate-800/50">
          <p className="text-xs text-slate-400 mb-2">{t('calc.starTrailsDuration')}</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-xs text-slate-500">{t('calc.short')}</p>
              <p className="font-mono text-white">{results.shortTrails}min</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">{t('calc.medium')}</p>
              <p className="font-mono text-amber-500">{results.mediumTrails}min</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">{t('calc.long')}</p>
              <p className="font-mono text-white">{results.longTrails}min</p>
            </div>
          </div>
        </div>

        <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
          <p className="text-xs text-slate-400">{t('calc.forOneHourStackedTrails')}</p>
          <p className="font-mono text-xl text-purple-400">
            ~{Math.ceil(results.shotsNeeded)} shots × {results.noTrails}s
          </p>
        </div>
      </div>
    </div>
  );
};

// Main Component
const PhotoCalculators = () => {
  const { t } = useI18n();
  return (
    <Card className="bg-slate-900/50 border-white/10">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-heading flex items-center gap-2">
          <Calculator className="w-5 h-5 text-cyan-400" />
          {t('calc.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="exposure" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-slate-800">
            <TabsTrigger value="exposure" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="calc-tab-exposure">
              <Aperture className="w-4 h-4 mr-1" />
              {t('calc.exposure')}
            </TabsTrigger>
            <TabsTrigger value="dof" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="calc-tab-dof">
              <Focus className="w-4 h-4 mr-1" />
              {t('calc.dof')}
            </TabsTrigger>
            <TabsTrigger value="trails" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="calc-tab-trails">
              <Star className="w-4 h-4 mr-1" />
              {t('calc.starTrails')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="exposure" className="mt-4">
            <ExposureCalculator />
          </TabsContent>
          <TabsContent value="dof" className="mt-4">
            <DOFCalculator />
          </TabsContent>
          <TabsContent value="trails" className="mt-4">
            <StarTrailsCalculator />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default PhotoCalculators;
